import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format fils (integer) to AED string
 * 100 fils = 1.00 AED
 */
export function formatMoney(fils: number): string {
  const aed = fils / 100
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 2
  }).format(aed)
}

/**
 * Parse AED string to fils (integer)
 */
export function parseMoneyToFils(value: string): number {
  const cleaned = value.replace(/[^\d.]/g, '')
  const aed = parseFloat(cleaned) || 0
  return Math.round(aed * 100)
}

/**
 * Format date to DD/MM/YYYY (UAE format)
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

/**
 * Format date to ISO string (YYYY-MM-DD) in UAE local time (GMT+4, no DST).
 *
 * Bare `date.toISOString().split('T')[0]` returns the UTC date, which is
 * off-by-one between 20:00–23:59 UAE local time (= 16:00–19:59 UTC →
 * still "today" in UTC) and 00:00–03:59 UAE local time (= 20:00–23:59
 * UTC previous day → "yesterday" in UTC). Every callsite using
 * `today()` to stamp `due_date`, `expense.date`, `sale.date`, etc.
 * was silently wrong at those hours.
 */
export function toISODate(date: Date): string {
  // Shift by +4h so that .toISOString() returns the UAE local Y-M-D.
  const uaeMs = date.getTime() + 4 * 60 * 60 * 1000
  return new Date(uaeMs).toISOString().split('T')[0]
}

/**
 * Get today's date as ISO string in UAE local time.
 */
export function today(): string {
  return toISODate(new Date())
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(dateString)
}

/**
 * Loose UAE phone-number validation. Accepts:
 *   - Local mobile: 05X XXXXXXX (10 digits starting 05)
 *   - Local landline: 0[234679] XXXXXXX (9 digits starting 0 + area code)
 *   - International: +971 5X XXXXXXX or +971 X XXXXXXX
 * Whitespace, dashes, and parens are ignored. Empty string returns true
 * (the field is optional).
 */
export function isLikelyUaePhone(raw: string): boolean {
  if (!raw || !raw.trim()) return true
  const digits = raw.replace(/[\s\-\(\)]/g, '')
  // Local 0-prefix: 9-10 digits total
  if (/^0\d{8,9}$/.test(digits)) return true
  // International +971: 11-12 chars total after the +971
  if (/^\+971\d{8,9}$/.test(digits)) return true
  return false
}

/**
 * Fetch an authenticated PDF endpoint and open the resulting blob in a
 * new tab. Plain <a href> won't work for PDF routes because the worker
 * reads the JWT from the `Authorization: Bearer …` header — the browser
 * doesn't attach that on a target=_blank navigation. Manually fetching
 * the bytes, wrapping in a blob URL, and opening that URL is the
 * standard workaround for Bearer-auth APIs serving binary content.
 *
 * Returns false (and shows an alert) if the fetch fails so the caller
 * can stop spinners/loading state.
 */
export async function openAuthenticatedPdf(apiPath: string, fallbackErrorMsg = 'Could not open document'): Promise<boolean> {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null
    // VITE_API_URL is the worker base when running in prod ("https://api.vansales…");
    // empty string in local dev where /api is proxied to the same origin.
    const apiBase = (import.meta as any).env?.VITE_API_URL || ''
    const fullUrl = apiPath.startsWith('http') ? apiPath : `${apiBase}${apiPath}`
    const res = await fetch(fullUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      if (typeof window !== 'undefined') alert(fallbackErrorMsg)
      return false
    }
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    window.open(blobUrl, '_blank')
    // Revoke after a delay so the new tab has time to load the bytes.
    // 60s is plenty even on slow mobile connections.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    return true
  } catch {
    if (typeof window !== 'undefined') alert(fallbackErrorMsg)
    return false
  }
}

/**
 * Fetch an authenticated PDF endpoint and save it as a file download
 * instead of opening a viewer tab. Same Bearer-auth blob flow as
 * openAuthenticatedPdf; the download is triggered via a temporary
 * anchor with the `download` attribute, which also sidesteps popup
 * blockers (no window.open involved).
 */
export async function downloadAuthenticatedPdf(
  apiPath: string,
  filename: string,
  fallbackErrorMsg = 'Could not download document'
): Promise<boolean> {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null
    const apiBase = (import.meta as any).env?.VITE_API_URL || ''
    const fullUrl = apiPath.startsWith('http') ? apiPath : `${apiBase}${apiPath}`
    const res = await fetch(fullUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      // These routes still answer with the standard JSON error envelope on
      // failure. Show the server's reason ("263 invoices exceed the …
      // limit") rather than a generic message the user can't act on.
      let msg = fallbackErrorMsg
      try {
        const body = await res.clone().json() as { error?: { message?: string } }
        if (body?.error?.message) msg = body.error.message
      } catch { /* not JSON — keep the fallback */ }
      if (typeof window !== 'undefined') alert(msg)
      return false
    }
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    return true
  } catch {
    if (typeof window !== 'undefined') alert(fallbackErrorMsg)
    return false
  }
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

/** Unpaid invoices falling due within this many days are flagged "due soon". */
export const DUE_SOON_DAYS = 7

export interface DueStatus {
  tone: 'overdue' | 'soon' | 'later'
  label: string
}

/**
 * How an unpaid invoice's due date reads today (UAE): "Overdue 12 days",
 * "Due today", "Due in 3 days" or "Due 24/10/2026". Due today is NOT overdue,
 * matching the aging report (overdue starts the day after). Null when there's
 * no due date — aging counts those as current.
 */
export function dueStatus(dueDate: string | null | undefined): DueStatus | null {
  if (!dueDate) return null
  const days = Math.round((Date.parse(dueDate.slice(0, 10)) - Date.parse(today())) / 86_400_000)
  if (Number.isNaN(days)) return null
  if (days < 0) {
    const late = -days
    return { tone: 'overdue', label: `Overdue ${late} day${late === 1 ? '' : 's'}` }
  }
  if (days === 0) return { tone: 'soon', label: 'Due today' }
  if (days <= DUE_SOON_DAYS) return { tone: 'soon', label: `Due in ${days} day${days === 1 ? '' : 's'}` }
  return { tone: 'later', label: `Due ${formatDate(dueDate)}` }
}

/** Text colour for a due status: red overdue, amber due soon, grey otherwise. */
export function dueToneClass(tone: DueStatus['tone']): string {
  return tone === 'overdue' ? 'text-danger-600 font-medium'
    : tone === 'soon' ? 'text-warning-600 font-medium'
    : 'text-gray-500'
}

/**
 * As downloadAuthenticatedPdf, but POSTs a JSON body — for endpoints whose
 * input is too big for a query string, such as the owner's batch invoice
 * ZIP (a list of invoice ids).
 *
 * Prefers the server's filename from Content-Disposition when it is
 * readable. That header is only visible to the page because the worker
 * lists it in the CORS exposeHeaders; if that ever regresses, the caller's
 * fallback name is used rather than the download arriving untitled.
 */
export async function downloadAuthenticatedPost(
  apiPath: string,
  body: unknown,
  fallbackFilename: string,
  fallbackErrorMsg = 'Could not download'
): Promise<{ ok: boolean; count?: number }> {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null
    const apiBase = (import.meta as any).env?.VITE_API_URL || ''
    const fullUrl = apiPath.startsWith('http') ? apiPath : `${apiBase}${apiPath}`
    const res = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      let msg = fallbackErrorMsg
      try {
        const b = await res.clone().json() as { error?: { message?: string } }
        if (b?.error?.message) msg = b.error.message
      } catch { /* not JSON — keep the fallback */ }
      if (typeof window !== 'undefined') alert(msg)
      return { ok: false }
    }
    const disposition = res.headers.get('Content-Disposition') || ''
    const match = /filename="([^"]+)"/.exec(disposition)
    const count = Number(res.headers.get('X-Invoice-Count')) || undefined
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = match?.[1] || fallbackFilename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
    return { ok: true, count }
  } catch {
    if (typeof window !== 'undefined') alert(fallbackErrorMsg)
    return { ok: false }
  }
}
